import { IconButton, SwipeableDrawer, useMediaQuery } from '@material-ui/core'
import { useAtom } from 'jotai'
import * as React from 'react'
import styled from 'styled-components'
import { rwNotificationFeedAtom } from '../../atoms/Notifications'
import { GetNotificationFeed } from '../../data/Notifications'
import { ActivityData, NotificationData } from '../../interfaces/Notifications'
import InfiniteScroll from 'react-infinite-scroller'
import { Spinner } from '@geist-ui/react'
import { SmallSpinner, SmallSpinnerContainer } from '../spinner'
import NotificationItem from './item/NotificationItem'
import { fbEnrichNotificationsFromActivity } from '../../data/FirebaseEnrich'
import { ChevronLeft } from '@geist-ui/react-icons'
import { useInfiniteLoadNotification } from '../../hooks/infiniteLoadNotification'
import useAuth from '../../hooks/auth'
import EmailNotificationInputItem from '../header/EmailNotificationsInput'

const PAGE_SIZE = 7

const NotificationDrawer = (): JSX.Element => {
  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent)
  const [address, setOpen] = useAtom(rwNotificationFeedAtom)
  const { profile } = useAuth()
  const isMobile = useMediaQuery('(max-width: 43em)')

  const fetchNotifications = React.useCallback(
    async ({
      offset,
      limit,
    }: {
      offset: number
      limit: number
    }): Promise<{ rawData: ActivityData[]; data: NotificationData[]; hasMore: boolean }> => {
      if (profile?.address) {
        const response = await GetNotificationFeed(profile.address, limit, offset)
        const notitications = await fbEnrichNotificationsFromActivity(response.notifications)
        return {
          rawData: response.notifications || [],
          data: notitications,
          hasMore: response.notifications?.length === PAGE_SIZE,
        }
      } else {
        return { rawData: [], data: [], hasMore: false }
      }
    },
    [profile?.address]
  )

  const { data, isLoading, isRefetching, nextIsLoading, fetchNext, hasNext, seenItem } =
    useInfiniteLoadNotification<NotificationData, ActivityData>({
      id: address ?? '',
      getItems: fetchNotifications,
      refetchMarker: profile?.address,
      page_size: PAGE_SIZE,
    })

  const handleLoadMore = React.useCallback(() => {
    if (isLoading || nextIsLoading) {
      return
    }

    fetchNext()
  }, [fetchNext, isLoading, nextIsLoading])

  React.useEffect(() => {
    if (profile?.address) {
      handleLoadMore()
    }
  }, [profile?.address, handleLoadMore])

  return (
    <SwipeableDrawer
      aria-labelledby="notification-drawer"
      open={address ? true : false}
      anchor="right"
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      onClose={(): void => {
        setOpen(undefined)
      }}
      onOpen={(): void => {
        console.log('open')
      }}
      PaperProps={{
        style: {
          backgroundColor: '#101010',
          borderRadius: isMobile ? '15px 0px 0px 15px' : '30px 0px 0px 30px',
          borderWidth: 1,
          borderColor: '#323232',
          borderStyle: 'solid',
          width: '25vw',
          minWidth: '400px',
        },
      }}
      BackdropProps={{
        style: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0,0,0,.1)',
        },
      }}
    >
      <OuterDiv>
        <TitleDiv>
          <IconButton size={'medium'} disableFocusRipple onClick={() => setOpen(undefined)}>
            <ChevronLeft color="#fff" size={26} />
          </IconButton>

          <Title>Notifications</Title>
        </TitleDiv>
        <EmailNotificationInputItem />
        {isLoading || isRefetching ? (
          <Center>
            <LargeSpinner />
          </Center>
        ) : !hasNext && !data.length ? (
          <LeftCenter>
            <NoItems>{`You don't have any notifications yet.`}</NoItems>
          </LeftCenter>
        ) : (
          <InfiniteScroll
            loadMore={handleLoadMore}
            hasMore={hasNext}
            loader={
              <SmallSpinnerContainer>
                <SmallSpinner />
              </SmallSpinnerContainer>
            }
            useWindow={false}
          >
            {data
              .filter(
                (notification) =>
                  notification.activity.verb !== 'error' && notification.activity.verb !== 'create'
              )
              .map((notification, index) => (
                <NotificationItem
                  key={`notification-item-${index}`}
                  notification={notification}
                  close={() => {
                    seenItem(notification.activity.group)
                    setOpen(undefined)
                  }}
                />
              ))}
          </InfiniteScroll>
        )}
      </OuterDiv>
    </SwipeableDrawer>
  )
}

export default NotificationDrawer

const OuterDiv = styled.div`
  width: calc(100% - 30px);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  /* margin: 30px 30px 30px 0; */
  margin-top: 30px;
`

const TitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 15px;
  margin-left: 15px;
`

const Title = styled.div`
  font-weight: 700;
  font-size: 24px;
  color: white;
  margin-left: 5px;
  width: fit-content;
`

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const LeftCenter = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 15px;
`

const LargeSpinner = styled(Spinner)`
  min-width: 96px;
  min-height: 96px;
`

const NoItems = styled.div`
  color: white;
  font-size: 18px;
`
